import { APP_BUILD_INFO } from '../buildInfo'
import { normalizeLocaleCode, type Locale } from '../i18n'
import {
   normalizeHabitsForStorage,
   serializeHabit,
   type Habit,
} from '../state/habitsStore'
import type { WeightEntry } from '../state/weightStore'
import {
   dedupeFastHistoryEntries,
   deserializeFastEntry,
   normalizeDisplayModePreference,
   prepareFastEntryForPersistence,
   sortFastEntriesDescending,
   type FastDisplayMode,
   type FastEntry,
} from '../utils/fastEntry'
import { parseTimestampToNumber } from '../utils/time'
import { normalizeUnitSystem, type UnitSystem } from '../utils/units'
import {
   deserializeWeightEntry,
   prepareWeightEntryForPersistence,
} from '../utils/weightEntry'

export const STORAGE_KEYS = {
   currentFast: 'currentFast',
   fastHistory: 'fastHistory',
   weightHistory: 'weightHistory',
   habits: 'habits',
   userProfile: 'userProfile',
   userId: 'userId',
   showWeightTarget: 'showWeightTarget',
   weightChartMode: 'weightChartMode',
   debugState: 'fastingDebugState',
   displayMode: 'timerDisplayMode',
} as const

export type SyncPersistMeta = {
   action?: string
   clientTimestamp?: number
   clientStateVersion?: number
   serverUpdatedAt?: number
   requestIp?: string
}

export type SyncDebugInfo = {
   lastPersist: SyncPersistMeta | null
   clientBuildInfo: typeof APP_BUILD_INFO
   serverBuildInfo: Record<string, unknown> | null
}

export type StoredUserProfile = {
   heightCm: number | null
   targetWeightKg: number | null
   targetMonthlyRunEffort: number | null
   preferredLocale: Locale | null
   unitSystem: UnitSystem
}

/**
 * Validates and trims sync last-persist metadata to known fields.
 *
 * @param meta - The raw metadata.
 * @returns The sanitized metadata, or null when empty.
 */
export function sanitizeLastPersistMeta(meta: unknown): SyncPersistMeta | null {
   if (!meta || typeof meta !== 'object') {
      return null
   }

   const record = meta as Record<string, unknown>
   const sanitized: SyncPersistMeta = {}
   if (typeof record.action === 'string' && record.action) {
      sanitized.action = record.action
   }
   if (
      typeof record.clientTimestamp === 'number' &&
      Number.isFinite(record.clientTimestamp)
   ) {
      sanitized.clientTimestamp = record.clientTimestamp
   }
   if (
      typeof record.clientStateVersion === 'number' &&
      Number.isFinite(record.clientStateVersion)
   ) {
      sanitized.clientStateVersion = record.clientStateVersion
   }
   const serverUpdatedAtMillis = parseTimestampToNumber(record.serverUpdatedAt)
   if (serverUpdatedAtMillis !== null) {
      sanitized.serverUpdatedAt = serverUpdatedAtMillis
   }
   if (typeof record.requestIp === 'string' && record.requestIp) {
      sanitized.requestIp = record.requestIp
   }

   return Object.keys(sanitized).length > 0 ? sanitized : null
}

/**
 * Reads and JSON-parses a localStorage value, returning null on missing or invalid data.
 *
 * @param key - The storage key.
 * @returns The parsed value, or null.
 */
function readJson(key: string): unknown {
   const raw = localStorage.getItem(key)
   if (!raw) {
      return null
   }
   try {
      return JSON.parse(raw)
   } catch (error) {
      console.error(`Failed to parse ${key} from storage`, error)
      return null
   }
}

/**
 * Loads and deserializes the stored current fast.
 *
 * @returns The current fast, or null.
 */
export function loadStoredCurrentFast(): FastEntry | null {
   return deserializeFastEntry(readJson(STORAGE_KEYS.currentFast))
}

/**
 * Loads, de-duplicates, and sorts the stored fast history.
 *
 * @returns The stored fast history, newest first.
 */
export function loadStoredFastHistory(): FastEntry[] {
   const parsed = readJson(STORAGE_KEYS.fastHistory)
   const entries =
      Array.isArray(parsed) ?
         parsed
            .map(entry => deserializeFastEntry(entry))
            .filter((entry): entry is FastEntry => entry !== null)
      :  []
   const deduped = dedupeFastHistoryEntries(entries)
   sortFastEntriesDescending(deduped)
   return deduped
}

/**
 * Loads and sorts the stored weight history newest first.
 *
 * @returns The stored weight entries.
 */
export function loadStoredWeightHistory(): WeightEntry[] {
   const parsed = readJson(STORAGE_KEYS.weightHistory)
   if (!Array.isArray(parsed)) {
      return []
   }
   return parsed
      .map(entry => deserializeWeightEntry(entry))
      .filter((entry): entry is WeightEntry => entry !== null)
      .sort((a, b) => b.timestamp - a.timestamp)
}

/**
 * Loads and normalizes the stored habits.
 *
 * @returns The stored habits.
 */
export function loadStoredHabits(): Habit[] {
   const parsed = readJson(STORAGE_KEYS.habits)
   if (!parsed) {
      return []
   }
   return normalizeHabitsForStorage(parsed)
}

/**
 * Loads the stored user profile (height, targets, locale, unit system).
 *
 * @returns The profile, or null when absent.
 */
export function loadStoredUserProfile(): StoredUserProfile | null {
   const parsed = readJson(STORAGE_KEYS.userProfile)
   if (!parsed || typeof parsed !== 'object') {
      return null
   }
   const record = parsed as Record<string, unknown>
   return {
      heightCm: typeof record.heightCm === 'number' ? record.heightCm : null,
      targetWeightKg:
         typeof record.targetWeightKg === 'number' ? record.targetWeightKg : null,
      targetMonthlyRunEffort:
         typeof record.targetMonthlyRunEffort === 'number' ?
            record.targetMonthlyRunEffort
         :  null,
      preferredLocale: normalizeLocaleCode(
         typeof record.preferredLocale === 'string' ? record.preferredLocale : null,
      ),
      unitSystem: normalizeUnitSystem(record.unitSystem),
   }
}

/**
 * Loads the stored 'show weight target' preference.
 *
 * @returns The preference, or null when unset.
 */
export function loadStoredShowWeightTarget(): boolean | null {
   const saved = localStorage.getItem(STORAGE_KEYS.showWeightTarget)
   if (saved === null) {
      return null
   }
   return saved !== 'false'
}

/**
 * Persists the 'show weight target' preference.
 *
 * @param value - Whether to show the target.
 */
export function persistShowWeightTarget(value: boolean) {
   localStorage.setItem(STORAGE_KEYS.showWeightTarget, value ? 'true' : 'false')
}

/**
 * Loads the stored weight-chart mode preference.
 *
 * @returns The mode, or null when unset/invalid.
 */
export function loadStoredWeightChartMode(): 'line' | 'scatter' | null {
   const saved = localStorage.getItem(STORAGE_KEYS.weightChartMode)
   return saved === 'line' || saved === 'scatter' ? saved : null
}

/**
 * Persists the weight-chart mode preference.
 *
 * @param mode - The chart mode.
 */
export function persistWeightChartMode(mode: 'line' | 'scatter') {
   localStorage.setItem(STORAGE_KEYS.weightChartMode, mode)
}

/**
 * Loads the stored timer display-mode preference.
 *
 * @returns The mode, or null when unset.
 */
export function loadStoredDisplayMode(): FastDisplayMode | null {
   try {
      return normalizeDisplayModePreference(
         localStorage.getItem(STORAGE_KEYS.displayMode),
      )
   } catch {
      return null
   }
}

/**
 * Persists the timer display-mode preference.
 *
 * @param mode - The display mode.
 */
export function persistDisplayMode(mode: FastDisplayMode) {
   try {
      localStorage.setItem(STORAGE_KEYS.displayMode, mode)
   } catch (error) {
      console.error('Failed to persist display mode preference', error)
   }
}

/**
 * Loads the stored sync debug info, defaulting when absent or invalid.
 *
 * @returns The sync debug info.
 */
export function loadStoredSyncDebugInfo(): SyncDebugInfo {
   const parsed = readJson(STORAGE_KEYS.debugState)
   if (!parsed || typeof parsed !== 'object') {
      return {
         lastPersist: null,
         clientBuildInfo: APP_BUILD_INFO,
         serverBuildInfo: null,
      }
   }
   const record = parsed as Record<string, unknown>
   return {
      lastPersist: sanitizeLastPersistMeta(record.lastPersist ?? record),
      clientBuildInfo: APP_BUILD_INFO,
      serverBuildInfo:
         record.serverBuildInfo && typeof record.serverBuildInfo === 'object' ?
            (record.serverBuildInfo as Record<string, unknown>)
         :  null,
   }
}

/**
 * Persists the sync debug info, stamping the current client build.
 *
 * @param info - The sync debug info to store.
 */
export function persistSyncDebugInfo(info: SyncDebugInfo) {
   info.clientBuildInfo = APP_BUILD_INFO
   localStorage.setItem(STORAGE_KEYS.debugState, JSON.stringify(info))
}

/**
 * Loads the stored user id (hashed sync key).
 *
 * @returns The stored user id, or null.
 */
export function loadStoredUserId(): string | null {
   return localStorage.getItem(STORAGE_KEYS.userId)
}

/**
 * Persists or clears the stored user id.
 *
 * @param userId - The user id, or null to clear.
 */
export function persistStoredUserId(userId: string | null) {
   if (userId) {
      localStorage.setItem(STORAGE_KEYS.userId, userId)
   } else {
      localStorage.removeItem(STORAGE_KEYS.userId)
   }
}

/**
 * Serializes and persists the full local app state (fasts, weights, habits, profile).
 *
 * @param state - The in-memory state to persist.
 */
export function saveAppState(state: {
   currentFast: FastEntry | null
   fastHistory: FastEntry[]
   weightHistory: WeightEntry[]
   habits: Habit[]
   userProfile: StoredUserProfile
}) {
   const serializedCurrentFast =
      state.currentFast ? prepareFastEntryForPersistence(state.currentFast) : null
   const serializedHistory = state.fastHistory
      .filter(entry => entry && typeof entry === 'object')
      .map(entry => prepareFastEntryForPersistence(entry))
      .filter(entry => entry !== null)
   const serializedWeights = state.weightHistory
      .filter(entry => entry && typeof entry === 'object')
      .map(entry => prepareWeightEntryForPersistence(entry))
      .filter(entry => entry !== null)
   const serializedHabits = state.habits
      .filter(entry => entry && typeof entry === 'object')
      .map(entry => serializeHabit(entry))

   localStorage.setItem(STORAGE_KEYS.currentFast, JSON.stringify(serializedCurrentFast))
   localStorage.setItem(STORAGE_KEYS.fastHistory, JSON.stringify(serializedHistory))
   localStorage.setItem(STORAGE_KEYS.weightHistory, JSON.stringify(serializedWeights))
   localStorage.setItem(STORAGE_KEYS.habits, JSON.stringify(serializedHabits))
   localStorage.setItem(STORAGE_KEYS.userProfile, JSON.stringify(state.userProfile))
}

/**
 * Clears all locally stored account data (fasts, weights, habits, profile).
 */
export function removeLocalAccountData() {
   localStorage.removeItem(STORAGE_KEYS.currentFast)
   localStorage.removeItem(STORAGE_KEYS.fastHistory)
   localStorage.removeItem(STORAGE_KEYS.weightHistory)
   localStorage.removeItem(STORAGE_KEYS.habits)
   localStorage.removeItem(STORAGE_KEYS.userProfile)
}
